import client from '@/utils/httpClient';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export interface ConferenceVirtualSettingsDto {
  id: string;
  conferenceId: string;
  virtualConferenceInfoPanelEnabled: boolean | null;
  virtualConferenceAudioRecording: boolean | null;
  virtualConferenceVideoRecording: boolean | null;
  virtualConferenceJoinUrl: string | null;
  virtualConferenceStartUrl: string | null;
}

export interface OrgVirtualSettingsDto {
  virtualConferenceInfoPanelEnabled: boolean;
  virtualConferenceAudioRecording: boolean;
}

export interface ConferenceVirtualSettingsWithContextDto {
  settings: ConferenceVirtualSettingsDto | null;
  hasVirtualConferenceLicense: boolean;
  orgDefaults: OrgVirtualSettingsDto;
}

export interface ConferenceVirtualSettingsForUpsert {
  virtualConferenceInfoPanelEnabled: boolean | null;
  virtualConferenceAudioRecording: boolean | null;
  virtualConferenceVideoRecording: boolean | null;
  virtualConferenceJoinUrl: string | null;
  virtualConferenceStartUrl: string | null;
}

const conferenceVirtualSettingsKeys = {
  base: ['conferencevirtualsettings'] as const,
  byConference: (conferenceId: string) => [
    ...conferenceVirtualSettingsKeys.base,
    'byConference',
    conferenceId,
  ],
};

function getConferenceVirtualSettings(conferenceId: string) {
  return client
    .get<ConferenceVirtualSettingsWithContextDto>(
      `/v2/conferencevirtualsettings/ByConference/${conferenceId}`,
    )
    .then((response) => response.data);
}

export function useGetConferenceVirtualSettings(conferenceId: string | undefined) {
  const definiteId = conferenceId ?? '';
  return useQuery(
    conferenceVirtualSettingsKeys.byConference(definiteId),
    () => getConferenceVirtualSettings(definiteId),
    {
      enabled: conferenceId !== undefined && conferenceId !== '',
    },
  );
}

function upsertConferenceVirtualSettings(
  conferenceId: string,
  data: ConferenceVirtualSettingsForUpsert,
) {
  return client
    .put<ConferenceVirtualSettingsDto>(
      `/v2/conferencevirtualsettings/ByConference/${conferenceId}`,
      data,
    )
    .then((response) => response.data);
}

export function useUpsertConferenceVirtualSettings(conferenceId: string) {
  const queryClient = useQueryClient();

  return useMutation(
    (data: ConferenceVirtualSettingsForUpsert) =>
      upsertConferenceVirtualSettings(conferenceId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(conferenceVirtualSettingsKeys.byConference(conferenceId));
      },
    },
  );
}

async function resetConferenceVirtualSettingsByOrganization(organizationId: string) {
  const response = await client.put(
    `/v2/conferencevirtualsettings/resetByOrganization/${organizationId}`,
  );
  return response.data;
}

export function useResetConferenceVirtualSettingsByOrganization() {
  const queryClient = useQueryClient();

  return useMutation(
    (organizationId: string) => resetConferenceVirtualSettingsByOrganization(organizationId),
    {
      onSuccess: () => {
        // Invalidate all conference virtual settings queries since this affects all conferences in the org
        queryClient.invalidateQueries(conferenceVirtualSettingsKeys.base);
      },
    },
  );
}
